import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_ROOT } from '@/routes/paths';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  Palette as ThemeIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SettingsCard } from '@/components/organization/SettingsCard';
import { OrganizationOverviewCard } from '@/components/organization/OrganizationOverviewCard';
import {
  EditOrgDetailsModal,
  EditBillingModal,
  EditWebsiteContactModal,
  UploadOrgPictureModal,
  CustomizeThemeModal,
  DeleteOrgConfirmationModal
} from 'src/components/organization';
import { useDemoOrgData } from '@/hooks/organization/useDemoOrgData';
import { DEF_JAM_ORG_ID } from '@/mockData/common/demoFilters';
import { notifyFeature } from '@/utils/demoNotifications';
import { useOrg } from 'src/contexts/OrgContext';
import { useDemoOrg } from 'src/contexts/DemoOrgContext';
import { getOrgApi, updateOrgApi, updateOrgThemeApi, deleteOrgApi, uploadOrgPictureApi } from 'src/api/organization';
import { uploadFileWithPresignedUrl } from 'src/api/external-api/s3';
import { toast } from 'sonner';
import { useProfile } from 'src/hooks/profile/useProfile';

interface OrganizationSettingsProps {
  isDemo?: boolean;
}

const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({ isDemo = false }) => {
  const navigate = useNavigate();
  const { getOrganizationById } = useDemoOrgData();

  // Get organization from context
  const { selectedOrgId } = useOrg();
  const demoOrgContext = useDemoOrg();
  const { profile } = useProfile();

  // State for production organization data
  const [organization, setOrganization] = useState<any>(null);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [showEditBillingModal, setShowEditBillingModal] = useState(false);
  const [showEditWebsiteModal, setShowEditWebsiteModal] = useState(false);
  const [showUploadPictureModal, setShowUploadPictureModal] = useState(false);
  const [showCustomizeThemeModal, setShowCustomizeThemeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Determine which orgId to use (personal org if no selectedOrgId)
  const orgId = isDemo
    ? (demoOrgContext?.selectedOrg?.orgId ?? DEF_JAM_ORG_ID)
    : (selectedOrgId || profile?.userId);

  // Fetch organization data in production mode
  useEffect(() => {
    if (isDemo) {
      // Demo mode: use demo data
      const demoOrg = getOrganizationById(orgId || DEF_JAM_ORG_ID);
      if (demoOrg) {
        setOrganization({
          ...demoOrg,
          website: getOrgWebsite(demoOrg.name),
          billingEmail: getOrgBillingEmail(demoOrg.name)
        });
      }
      setLoading(false);
    } else if (orgId) {
      // Production mode: fetch from API
      setLoading(true);
      getOrgApi({ orgId })
        .then((response) => {
          setOrganization(response.org);
          setLoading(false);
          setError(null);
        })
        .catch((err) => {
          console.error('Error fetching organization:', err);
          setError(err.message || 'Failed to load organization');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [orgId, isDemo, profile?.userId]);

  // Modal handlers
  const handleEditDetails = async (data: { name: string; type: string; description?: string }) => {
    if (!orgId) return;
    await updateOrgApi({ orgId, ...data });
    // Refresh organization data
    const response = await getOrgApi({ orgId });
    setOrganization(response.org);
  };

  const handleEditBilling = async (data: { billingEmail: string }) => {
    if (!orgId) return;
    await updateOrgApi({ orgId, ...data });
    // Refresh organization data
    const response = await getOrgApi({ orgId });
    setOrganization(response.org);
  };

  const handleEditWebsite = async (data: { website?: string }) => {
    if (!orgId) return;
    await updateOrgApi({ orgId, ...data });
    // Refresh organization data
    const response = await getOrgApi({ orgId });
    setOrganization(response.org);
  };

  // BUG #217 — was a notifyFeature stub even though /orgs/uploadOrgPicture
  // has been live since the #216 frontend↔backend route alignment landed.
  // Mirrors the profile-picture upload flow: presigned-POST from the API,
  // multipart PUT to S3 via the shared helper, then refresh the org so
  // the new logo URL flows back into the overview card.
  const handleUploadPicture = async (file: File) => {
    if (!orgId) return;
    try {
      const presigned = await uploadOrgPictureApi(orgId);
      if (!presigned.urlInfo) {
        toast.error('Could not get upload URL');
        return;
      }
      await uploadFileWithPresignedUrl(file, {
        url: presigned.urlInfo.url,
        fields: presigned.urlInfo.fields as Record<string, string>,
      } as Parameters<typeof uploadFileWithPresignedUrl>[1]);
      const response = await getOrgApi({ orgId });
      setOrganization(response.org);
      toast.success('Organization logo updated');
    } catch {
      toast.error('Failed to upload organization logo');
    }
  };

  const handleCustomizeTheme = async (data: { primaryColor?: string; secondaryColor?: string; accentColor?: string }) => {
    if (!orgId) return;
    await updateOrgThemeApi({ orgId, theme: data });
    // Optionally refresh theme data
  };

  const handleDeleteOrg = async () => {
    if (!orgId) return;
    await deleteOrgApi({ orgId });
    // Navigate back to organizations list
    navigate(PATH_ROOT.organization.organizations);
  };

  // Helper functions to generate realistic website and billing email
  function getOrgWebsite(orgName: string): string {
    switch (orgName) {
      case 'JELEEL!': return 'https://jeleel.com';
      case 'FOMO.IRL': return 'https://fomo.irl';
      case 'Def Jam Recordings': return 'https://defjam.com';
      case 'NFL': return 'https://nfl.com';
      case 'Recording Academy': return 'https://recordingacademy.com';
      default: return `https://${orgName.toLowerCase().replace(/\s+/g, '')}.com`;
    }
  }

  function getOrgBillingEmail(orgName: string): string {
    switch (orgName) {
      case 'JELEEL!': return 'billing@jeleel.com';
      case 'FOMO.IRL': return 'billing@fomo.irl';
      case 'Def Jam Recordings': return 'billing@defjam.com';
      case 'NFL': return 'billing@nfl.com';
      case 'Recording Academy': return 'billing@recordingacademy.com';
      default: return `billing@${orgName.toLowerCase().replace(/\s+/g, '')}.com`;
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizations : PATH_ROOT.organization.organizations)}
            className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl font-semibold text-foreground">Loading...</h1>
          <p className="text-muted-foreground mt-1">Fetching organization details</p>
        </div>
        <div className="text-center py-16">
          <div className="animate-pulse text-muted-foreground">
            Loading organization settings...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizations : PATH_ROOT.organization.organizations)}
            className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl font-semibold text-foreground">Error</h1>
          <p className="text-muted-foreground mt-1">Failed to load organization</p>
        </div>
        <div className="text-center py-16">
          <div className="text-red-400 mb-4">{error}</div>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            size="default"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // If organization not found, show error or redirect
  if (!organization) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizations : PATH_ROOT.organization.organizations)}
            className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl font-semibold text-foreground">Organization Not Found</h1>
          <p className="text-muted-foreground mt-1">The organization you're looking for doesn't exist.</p>
        </div>
        <div className="text-center py-16">
          <p className="text-muted-foreground">Organization not found.</p>
        </div>
      </div>
    );
  }

  const settingsItems = [
    {
      title: 'Organization Details',
      description: 'Edit organization name, description, and type information',
      icon: <BusinessIcon />,
      action: () => isDemo ? notifyFeature('Edit organization details', isDemo) : setShowEditDetailsModal(true),
      category: 'Basic Information'
    },
    {
      title: 'Billing Information',
      description: 'Manage billing email and payment methods',
      icon: <EmailIcon />,
      action: () => isDemo ? notifyFeature('Edit billing information', isDemo) : setShowEditBillingModal(true),
      category: 'Billing'
    },
    {
      title: 'Website & Contact',
      description: 'Update organization website and contact information',
      icon: <WebsiteIcon />,
      action: () => isDemo ? notifyFeature('Edit website & contact', isDemo) : setShowEditWebsiteModal(true),
      category: 'Contact Information'
    },
    {
      title: 'Profile Picture',
      description: 'Upload and manage organization profile picture',
      icon: <UploadIcon />,
      action: () => isDemo ? notifyFeature('Upload profile picture', isDemo) : setShowUploadPictureModal(true),
      category: 'Appearance'
    },
    {
      title: 'Organization Theme',
      description: 'Customize colors and branding for your organization',
      icon: <ThemeIcon />,
      action: () => isDemo ? notifyFeature('Customize theme', isDemo) : setShowCustomizeThemeModal(true),
      category: 'Appearance'
    },
    {
      title: 'Delete Organization',
      description: 'Permanently delete this organization and all associated data',
      icon: <DeleteIcon />,
      action: () => isDemo ? notifyFeature('Delete organization', isDemo) : setShowDeleteModal(true),
      category: 'Danger Zone',
      dangerous: true
    }
  ];


  // Group settings by category
  const groupedSettings = settingsItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, typeof settingsItems>);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-8">
        <button
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationManagement(orgId || '') : PATH_ROOT.organization.management)}
          className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-2xl font-semibold text-foreground">Organization Settings</h1>
        <p className="text-muted-foreground mt-1">Manage settings and configurations for {organization.name}</p>
      </div>

        {/* Organization overview card */}
        <OrganizationOverviewCard organization={organization} />

        {/* Settings sections */}
        {Object.entries(groupedSettings).map(([category, items]) => (
          <div key={category} className="mb-8">
            <h2 className="text-foreground text-lg font-semibold mb-4 px-1">
              {category}
            </h2>

            <div className="space-y-4">
              {items.map((item, index) => (
                <SettingsCard
                  key={index}
                  title={item.title}
                  description={item.description}
                  icon={item.icon}
                  onClick={item.action}
                  dangerous={item.dangerous}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Back to dashboard */}
        <div className="mt-8">
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationManagement(orgId || '') : PATH_ROOT.organization.management)}
          >
            Back to Management
          </Button>
        </div>

        {/* Modals */}
        {showEditDetailsModal && (
          <EditOrgDetailsModal
            onClose={() => setShowEditDetailsModal(false)}
            onSubmit={handleEditDetails}
            initialData={{
              name: organization.name,
              type: organization.type,
              description: organization.description
            }}
          />
        )}

        {showEditBillingModal && (
          <EditBillingModal
            onClose={() => setShowEditBillingModal(false)}
            onSubmit={handleEditBilling}
            initialData={{
              billingEmail: organization.billingEmail || ''
            }}
          />
        )}

        {showEditWebsiteModal && (
          <EditWebsiteContactModal
            onClose={() => setShowEditWebsiteModal(false)}
            onSubmit={handleEditWebsite}
            initialData={{
              website: organization.website
            }}
          />
        )}

        {showUploadPictureModal && (
          <UploadOrgPictureModal
            onClose={() => setShowUploadPictureModal(false)}
            onSubmit={handleUploadPicture}
            currentLogoUrl={organization.logoUrl}
          />
        )}

        {showCustomizeThemeModal && (
          <CustomizeThemeModal
            onClose={() => setShowCustomizeThemeModal(false)}
            onSubmit={handleCustomizeTheme}
            initialData={{
              primaryColor: organization.theme?.primaryColor,
              secondaryColor: organization.theme?.secondaryColor,
              accentColor: organization.theme?.accentColor
            }}
          />
        )}

        {showDeleteModal && (
          <DeleteOrgConfirmationModal
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteOrg}
            organizationName={organization.name}
          />
        )}
    </div>
  );
};

export default OrganizationSettings;
